import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef, Row } from '@/components/UI/dataTableConfig';
import { Badge, Button, Callout, ConfirmationDialog, Disclosure, ExpandableDataTable, FormSwitch, SegmentedControl } from '@/components/UI';
import { trpc, trpcClient } from '@/api/trpc';
import { showToast } from '@/components/Layout/Toast';
import { formatTimeHorizon } from '@/utils/timeHorizonGenerator';
import { FaPlay, FaProjectDiagram, FaRedo, FaStar, FaSync } from 'react-icons/fa';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import type { FeatureInteractionCandidate, FeatureInteractionRun, ParameterDiscoveryMetric } from './types';

dayjs.extend(relativeTime);

type FeatureInteractionScanDepth = 'fast' | 'deep' | 'exhaustive';
type InteractionSide = FeatureInteractionCandidate['sides'][number];

interface FeatureInteractionFindingsCardProps {
    experimentId: number;
    metric: ParameterDiscoveryMetric;
    selectedDuration: string;
    selectedHorizon: number;
}

const scanDepthOptions: { value: FeatureInteractionScanDepth; label: string }[] = [
    { value: 'fast', label: 'Fast' },
    { value: 'deep', label: 'Deep' },
    { value: 'exhaustive', label: 'Exhaustive' }
];

const scanDepthLabels: Record<FeatureInteractionScanDepth, string> = {
    fast: 'Fast',
    deep: 'Deep',
    exhaustive: 'Exhaustive'
};

const scanDepthFromPolicy = (policy: string | null | undefined): FeatureInteractionScanDepth | null => {
    if (policy === 'interaction-v4-fast') return 'fast';
    if (policy === 'interaction-v4-deep') return 'deep';
    if (policy === 'interaction-v4-exhaustive') return 'exhaustive';
    if (policy === 'interaction-v3-fast') return 'fast';
    if (policy === 'interaction-v3-deep') return 'deep';
    if (policy === 'interaction-v3-exhaustive') return 'exhaustive';
    return null;
};

const scanDepthLabelFromPolicy = (policy: string): string => {
    const depth = scanDepthFromPolicy(policy);
    return depth ? scanDepthLabels[depth] : policy;
};

const formatCount = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('en-US').format(value);
};

const formatMetricPercent = (value: number | null | undefined, digits = 2): string => {
    if (value === null || value === undefined || !Number.isFinite(value)) return '-';
    return `${value >= 0 ? '+' : ''}${value.toFixed(digits)}%`;
};

const formatFractionPercent = (value: number | null | undefined, digits = 1): string => {
    if (value === null || value === undefined || !Number.isFinite(value)) return '-';
    return `${(value * 100).toFixed(digits)}%`;
};

const formatProbability = (value: number | null | undefined): string => {
    if (value === null || value === undefined || !Number.isFinite(value)) return '-';
    if (value > 0 && value < 0.001) return '<0.001';
    return value.toFixed(3);
};

const titleCase = (value: string): string =>
    value
        .split('_')
        .map((part) => (part ? `${part[0]?.toUpperCase()}${part.slice(1)}` : part))
        .join(' ');

const getRunStatusVariant = (run: FeatureInteractionRun | null | undefined): 'secondary' | 'success' | 'warning' | 'danger' => {
    if (!run) return 'secondary';
    if (run.status === 'completed') return 'success';
    if (run.status === 'pending' || run.status === 'running') return 'warning';
    if (run.status === 'failed' || run.status === 'cancelled') return 'danger';
    return 'secondary';
};

const getRelationshipVariant = (
    relationship: FeatureInteractionCandidate['relationship']
): 'success' | 'warning' | 'danger' | 'secondary' => {
    if (relationship === 'synergy') return 'success';
    if (relationship === 'redundant') return 'warning';
    if (relationship === 'negative') return 'danger';
    return 'secondary';
};

const getActionabilityVariant = (
    actionability: FeatureInteractionCandidate['actionability']
): 'success' | 'warning' | 'danger' | 'secondary' | 'dark' => {
    if (actionability === 'actionable') return 'success';
    if (actionability === 'alias') return 'danger';
    if (actionability === 'redundant') return 'warning';
    if (actionability === 'weak') return 'secondary';
    return 'dark';
};

const selectorLabel = (side: InteractionSide): string => {
    if (side.selectorType === 'range') {
        const min = side.selectorMin === null ? '-inf' : side.selectorMin.toFixed(2);
        const max = side.selectorMax === null ? '+inf' : side.selectorMax.toFixed(2);
        return `${min} to ${max}`;
    }

    if (side.selectorValueBoolean !== null) return String(side.selectorValueBoolean);
    if (side.selectorValueNumber !== null) return side.selectorValueNumber.toFixed(2);
    return side.selectorValueString ?? side.bucketLabel;
};

const featureLabel = (side: InteractionSide | undefined): string => side?.featureLabel ?? side?.featureKey ?? '-';

const pairSearchText = (candidate: FeatureInteractionCandidate): string => {
    const [a, b] = candidate.sides;
    return [featureLabel(a), a?.bucketLabel, featureLabel(b), b?.bucketLabel].filter(Boolean).join(' ');
};

const getIncrementalLift = (candidate: FeatureInteractionCandidate): number | null =>
    candidate.incrementalLiftOverReferenceMarginal ?? candidate.incrementalLiftOverBestMarginal ?? candidate.interactionDelta;

const signColorClass = (value: number | null | undefined): string => {
    if (value === null || value === undefined || !Number.isFinite(value)) return '';
    return value >= 0 ? 'text-green-400' : 'text-red-400';
};

const SignedPct = ({ value, digits = 2 }: { value: number | null | undefined; digits?: number }) => {
    if (value === null || value === undefined || !Number.isFinite(value)) return <span className="text-zinc-500">-</span>;
    return <span className={value >= 0 ? 'text-green-400' : 'text-red-400'}>{formatMetricPercent(value, digits)}</span>;
};

const FeatureSide = ({ side }: { side: InteractionSide }) => (
    <div className="min-w-0 rounded border border-zinc-800 bg-zinc-950/60 px-3 py-2">
        <div className="truncate text-sm font-medium text-zinc-100" title={side.featureLabel ?? side.featureKey}>
            {side.featureLabel ?? side.featureKey}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
            <span className="max-w-full truncate rounded bg-zinc-800 px-2 py-0.5 text-zinc-200" title={side.bucketLabel}>
                {side.bucketLabel}
            </span>
            <span className="text-zinc-500">{selectorLabel(side)}</span>
        </div>
        <div className="mt-1 text-xs text-zinc-500">
            Marginal {formatMetricPercent(side.marginalLift)} / {formatCount(side.marginalEntryCount)} entries
        </div>
    </div>
);

const MetricCell = ({ label, value, className = '' }: { label: string; value: string; className?: string }) => (
    <div className="min-w-[86px]">
        <div className="text-xs text-zinc-500">{label}</div>
        <div className={`text-sm font-semibold text-zinc-100 ${className}`}>{value}</div>
    </div>
);

const PairCell = ({ candidate }: { candidate: FeatureInteractionCandidate }) => {
    const [a, b] = candidate.sides;

    return (
        <div className="min-w-0">
            <div className="flex items-center gap-1.5">
                {candidate.confidenceTier === 'high' && <FaStar className="shrink-0 text-yellow-500" size={11} title="High confidence" />}
                <span className="truncate text-sm font-medium text-zinc-100" title={`${featureLabel(a)} · ${a?.bucketLabel ?? ''}`}>
                    {featureLabel(a)} <span className="text-zinc-500">· {a?.bucketLabel}</span>
                </span>
            </div>
            <div className="mt-0.5 truncate text-xs text-zinc-500" title={`${featureLabel(b)} · ${b?.bucketLabel ?? ''}`}>
                <span className="text-zinc-600">×</span> {featureLabel(b)} · {b?.bucketLabel}
            </div>
        </div>
    );
};

const InteractionDetail = ({ candidate }: { candidate: FeatureInteractionCandidate }) => {
    const incrementalLift = getIncrementalLift(candidate);
    const holdoutIncremental = candidate.holdoutIncrementalLiftOverReferenceMarginal;
    const reason = candidate.rejectionReason ?? candidate.primaryReason;

    return (
        <div className="space-y-3 p-4">
            <div className="grid gap-2 lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] lg:items-stretch">
                <FeatureSide side={candidate.sides[0]} />
                <div className="flex items-center justify-center text-sm font-semibold text-zinc-500">+</div>
                <FeatureSide side={candidate.sides[1]} />
            </div>
            <div className="grid grid-cols-2 gap-x-5 gap-y-3 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-9">
                <MetricCell className="text-green-400" label="Joint Lift" value={formatMetricPercent(candidate.jointLift)} />
                <MetricCell
                    className={signColorClass(incrementalLift)}
                    label="Ref Increment"
                    value={formatMetricPercent(incrementalLift)}
                />
                <MetricCell
                    className={signColorClass(holdoutIncremental)}
                    label="Holdout Inc"
                    value={formatMetricPercent(holdoutIncremental)}
                />
                <MetricCell label="Q" value={formatProbability(candidate.interactionQValue)} />
                <MetricCell label="Stability" value={formatFractionPercent(candidate.stabilityDirectionAgreement, 0)} />
                <MetricCell label="CV Stable" value={formatFractionPercent(candidate.cvDirectionAgreement, 0)} />
                <MetricCell label="Validation" value={formatFractionPercent(candidate.validationScore, 0)} />
                <MetricCell label="Joint Entries" value={formatCount(candidate.jointEntryCount)} />
                <MetricCell label="Holdout Entries" value={formatCount(candidate.holdoutEntryCount)} />
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs">
                <Badge variant="dark">{titleCase(candidate.roleHint)}</Badge>
                <Badge variant={candidate.confidenceTier === 'high' ? 'success' : 'secondary'}>
                    {candidate.confidenceTier === 'high' ? 'High confidence' : 'Standard confidence'}
                </Badge>
                {reason && <span className="text-zinc-500">{reason}</span>}
            </div>
        </div>
    );
};

const renderInteractionDetail = ({ row }: { row: Row<FeatureInteractionCandidate> }) => <InteractionDetail candidate={row.original} />;

const featureInteractionColumns: ColumnDef<FeatureInteractionCandidate>[] = [
    {
        id: 'pair',
        header: 'Pair',
        accessorFn: (candidate) => pairSearchText(candidate),
        size: 240,
        minSize: 200,
        cell: ({ row }) => <PairCell candidate={row.original} />
    },
    {
        id: 'relationship',
        header: 'Relationship',
        accessorKey: 'relationship',
        size: 120,
        cell: ({ row }) => <Badge variant={getRelationshipVariant(row.original.relationship)}>{titleCase(row.original.relationship)}</Badge>
    },
    {
        id: 'verdict',
        header: 'Verdict',
        accessorKey: 'actionability',
        size: 120,
        cell: ({ row }) => (
            <Badge variant={getActionabilityVariant(row.original.actionability)}>{titleCase(row.original.actionability)}</Badge>
        )
    },
    {
        id: 'jointLift',
        header: 'Joint Lift',
        accessorFn: (candidate) => candidate.jointLift,
        size: 95,
        cell: ({ row }) => <SignedPct value={row.original.jointLift} />
    },
    {
        id: 'incremental',
        header: 'Incremental',
        accessorFn: (candidate) => getIncrementalLift(candidate),
        size: 105,
        cell: ({ row }) => <SignedPct value={getIncrementalLift(row.original)} />
    },
    {
        id: 'holdout',
        header: 'Holdout',
        accessorFn: (candidate) => candidate.holdoutIncrementalLiftOverReferenceMarginal,
        size: 95,
        cell: ({ row }) => <SignedPct value={row.original.holdoutIncrementalLiftOverReferenceMarginal} />
    },
    {
        id: 'q',
        header: 'Q',
        accessorFn: (candidate) => candidate.interactionQValue,
        size: 75,
        cell: ({ row }) => <span className="text-zinc-300">{formatProbability(row.original.interactionQValue)}</span>
    },
    {
        id: 'entries',
        header: 'Entries',
        accessorFn: (candidate) => candidate.jointEntryCount,
        size: 85,
        cell: ({ row }) => <span className="text-zinc-200">{formatCount(row.original.jointEntryCount)}</span>
    }
];

const RunSummary = ({ run }: { run: FeatureInteractionRun }) => (
    <div className="grid gap-3 py-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-9">
        <MetricCell label="Depth" value={scanDepthLabelFromPolicy(run.candidatePolicyVersion)} />
        <MetricCell label="Eligible Atoms" value={formatCount(run.eligibleAtomCount)} />
        <MetricCell label="Selected Atoms" value={formatCount(run.candidateAtomCount)} />
        <MetricCell label="Co-occurring Pairs" value={formatCount(run.candidatePairCount)} />
        <MetricCell label="Low Sample Rejects" value={formatCount(run.rejectedLowSamplePairCount)} />
        <MetricCell label="Pairs Evaluated" value={formatCount(run.evaluatedPairCount)} />
        <MetricCell label="Actionable" value={formatCount(run.actionablePairCount)} />
        <MetricCell label="Redundant/Alias" value={formatCount(run.redundantPairCount + run.aliasPairCount)} />
        <MetricCell label="Completed" value={run.completedAt ? new Date(run.completedAt).toLocaleString() : '-'} />
    </div>
);

const FeatureInteractionActions = ({
    isActiveRun,
    isPending,
    run,
    runStatus,
    scanDepth,
    showBroaderFindings,
    onRunClick,
    onScanDepthChange,
    onShowBroaderFindingsChange
}: {
    isActiveRun: boolean;
    isPending: boolean;
    run: FeatureInteractionRun | null;
    runStatus: string;
    scanDepth: FeatureInteractionScanDepth;
    showBroaderFindings: boolean;
    onRunClick: () => void;
    onScanDepthChange: (value: FeatureInteractionScanDepth) => void;
    onShowBroaderFindingsChange: (value: boolean) => void;
}) => {
    const isCompleted = run?.status === 'completed';
    const actionLabel = isCompleted ? 'Recompute' : 'Run Scan';
    const actionIcon = isCompleted ? <FaRedo /> : <FaPlay />;

    return (
        <div className="flex flex-wrap items-center gap-3">
            <SegmentedControl
                disabled={isActiveRun || isPending}
                options={scanDepthOptions}
                value={scanDepth}
                onChange={onScanDepthChange}
            />
            <FormSwitch
                checked={showBroaderFindings}
                className="mb-0"
                label="Show broader findings"
                labelClassName="text-sm text-zinc-300"
                onChange={(event) => onShowBroaderFindingsChange(event.target.checked)}
            />
            <Button
                disabled={isActiveRun || isPending}
                icon={isPending || isActiveRun ? <FaSync className="animate-spin" /> : actionIcon}
                isLoading={isPending}
                size="sm"
                variant={isCompleted ? 'secondary' : 'primary'}
                onClick={onRunClick}
            >
                {isActiveRun ? titleCase(runStatus) : actionLabel}
            </Button>
        </div>
    );
};

const FeatureInteractionStatusMessages = ({
    errorMessage,
    isError,
    isLoading,
    run
}: {
    errorMessage: string | undefined;
    isError: boolean;
    isLoading: boolean;
    run: FeatureInteractionRun | null;
}) => {
    if (isError) {
        return (
            <Callout title="Feature pair report failed" variant="error">
                {errorMessage}
            </Callout>
        );
    }

    if (!isLoading && !run) {
        return (
            <Callout title="No scan for this scope" variant="info">
                Queue a scan to look for useful pairings after Parameter Discovery evidence is ready.
            </Callout>
        );
    }

    if (!isLoading && run?.status === 'failed') {
        return (
            <Callout title="Last scan failed" variant="warning">
                {run.errorMessage ?? 'The scheduled runner can retry this scope when it is queued again.'}
            </Callout>
        );
    }

    return null;
};

const FeatureInteractionResults = ({
    candidates,
    isLoading,
    run,
    showBroaderFindings
}: {
    candidates: FeatureInteractionCandidate[];
    isLoading: boolean;
    run: FeatureInteractionRun | null;
    showBroaderFindings: boolean;
}) => {
    if (isLoading) {
        return (
            <div className="flex items-center gap-2 text-sm text-zinc-400">
                <FaSync className="animate-spin" />
                Loading feature pair findings
            </div>
        );
    }

    if (candidates.length > 0) {
        return (
            <ExpandableDataTable
                allowOverflow
                search
                sort
                striped
                columns={featureInteractionColumns}
                data={candidates}
                initialSorting={[{ id: 'incremental', desc: true }]}
                pagination={{ enabled: candidates.length > 15, pageSize: 15 }}
                renderSubComponent={renderInteractionDetail}
            />
        );
    }

    if (run?.status !== 'completed') return null;

    return (
        <Callout title="No findings matched the current filter" variant="info">
            {showBroaderFindings ? 'No stored pair findings matched this view.' : 'No actionable high-confidence pair findings were found.'}
        </Callout>
    );
};

const buildSummaryText = (run: FeatureInteractionRun | null, runStatus: string, isActiveRun: boolean): string => {
    if (run?.status === 'completed') {
        const parts = [`${formatCount(run.actionablePairCount)} actionable`, scanDepthLabelFromPolicy(run.candidatePolicyVersion)];
        if (run.completedAt) parts.push(dayjs(run.completedAt).fromNow());
        return parts.join(' · ');
    }

    if (isActiveRun) return 'Scan in progress';
    if (run?.status === 'failed') return 'Last scan failed';
    if (runStatus === 'not_scanned' || !run) return 'No scan yet';
    return titleCase(runStatus);
};

const FeatureInteractionFindingsCard = ({
    experimentId,
    metric,
    selectedDuration,
    selectedHorizon
}: FeatureInteractionFindingsCardProps) => {
    const queryClient = useQueryClient();
    const [scanDepth, setScanDepth] = useState<FeatureInteractionScanDepth>('deep');
    const [showBroaderFindings, setShowBroaderFindings] = useState(false);
    const [confirmRecomputeOpen, setConfirmRecomputeOpen] = useState(false);
    const [confirmExhaustiveOpen, setConfirmExhaustiveOpen] = useState(false);
    const [pendingForceRerun, setPendingForceRerun] = useState(false);

    const actionabilities: FeatureInteractionCandidate['actionability'][] = showBroaderFindings
        ? ['actionable', 'weak', 'redundant', 'alias', 'diagnostic_only']
        : ['actionable'];

    const queryInput = {
        experimentId,
        duration: selectedDuration,
        horizonSeconds: selectedHorizon,
        metric,
        scanDepth,
        highConfidenceOnly: !showBroaderFindings,
        actionabilities,
        includeWeak: showBroaderFindings,
        limit: showBroaderFindings ? 50 : 10
    };

    const interactionQueryOptions = trpc.experiment.getFeatureInteractions.queryOptions(queryInput);

    const interactionsQuery = useQuery({
        ...interactionQueryOptions,
        staleTime: 30000,
        refetchInterval: (query) => {
            const { data } = query.state;
            const status = data?.availability.run?.status;
            return status === 'pending' || status === 'running' ? 5000 : false;
        }
    });

    const run = interactionsQuery.data?.availability.run ?? null;
    const runStatus = run?.status ?? 'not_scanned';
    const isActiveRun = runStatus === 'pending' || runStatus === 'running';

    const requestMutation = useMutation({
        mutationFn: (params: { forceRerun: boolean }) =>
            trpcClient.experiment.requestFeatureInteractionRun.mutate({
                experimentId,
                duration: selectedDuration,
                horizonSeconds: selectedHorizon,
                metric,
                scanDepth,
                forceRerun: params.forceRerun
            }),
        onSuccess: (result) => {
            showToast(result.message, result.success ? (result.queued ? 'success' : 'info') : 'warning');
            void queryClient.invalidateQueries({ queryKey: interactionQueryOptions.queryKey });
            setConfirmRecomputeOpen(false);
            setConfirmExhaustiveOpen(false);
            setPendingForceRerun(false);
        },
        onError: (error: unknown) => {
            const typedError = error as { message?: string };
            showToast(typedError.message || 'Failed to queue feature-pair discovery', 'error');
        }
    });

    const candidates = interactionsQuery.data?.candidates ?? [];

    const handleRunClick = () => {
        const forceRerun = run?.status === 'completed';

        if (scanDepth === 'exhaustive') {
            setPendingForceRerun(forceRerun);
            setConfirmExhaustiveOpen(true);
            return;
        }

        if (forceRerun) {
            setConfirmRecomputeOpen(true);
            return;
        }

        requestMutation.mutate({ forceRerun: false });
    };

    const titleNode = (
        <span className="flex items-center gap-2">
            <FaProjectDiagram className="text-zinc-400" />
            <span className="font-semibold text-white">Feature Pair Findings</span>
        </span>
    );

    const badgeNode = (
        <span className="flex flex-wrap items-center gap-2">
            <Badge variant={getRunStatusVariant(run)}>{titleCase(runStatus)}</Badge>
            <span className="text-xs font-normal text-zinc-500">{buildSummaryText(run, runStatus, isActiveRun)}</span>
        </span>
    );

    return (
        <div>
            <Disclosure badge={badgeNode} className="bg-zinc-900" defaultExpanded={false} title={titleNode} variant="card">
                <div className="space-y-4">
                    <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                        <div className="text-sm text-zinc-500">
                            {metric === 'options' ? 'Options' : 'Stock'} / {selectedDuration} / {formatTimeHorizon(selectedHorizon)}
                        </div>
                        <FeatureInteractionActions
                            isActiveRun={isActiveRun}
                            isPending={requestMutation.isPending}
                            run={run}
                            runStatus={runStatus}
                            scanDepth={scanDepth}
                            showBroaderFindings={showBroaderFindings}
                            onRunClick={handleRunClick}
                            onScanDepthChange={setScanDepth}
                            onShowBroaderFindingsChange={setShowBroaderFindings}
                        />
                    </div>

                    <FeatureInteractionStatusMessages
                        errorMessage={interactionsQuery.error?.message}
                        isError={interactionsQuery.isError}
                        isLoading={interactionsQuery.isLoading}
                        run={run}
                    />
                    <FeatureInteractionResults
                        candidates={candidates}
                        isLoading={interactionsQuery.isLoading}
                        run={run}
                        showBroaderFindings={showBroaderFindings}
                    />

                    {!interactionsQuery.isError && run && (
                        <Disclosure className="border-t border-zinc-800 pt-2" title="Scan details" variant="plain">
                            <RunSummary run={run} />
                        </Disclosure>
                    )}
                </div>
            </Disclosure>

            <ConfirmationDialog
                confirmText="Recompute"
                confirmVariant="warning"
                isConfirmLoading={requestMutation.isPending}
                isOpen={confirmRecomputeOpen}
                message={`This will replace the current ${scanDepthLabels[scanDepth]} feature-pair run with a pending recompute for the scheduled runner.`}
                title="Recompute Feature Pair Findings"
                onCancel={() => setConfirmRecomputeOpen(false)}
                onConfirm={() => requestMutation.mutate({ forceRerun: true })}
            />
            <ConfirmationDialog
                confirmText={pendingForceRerun ? 'Recompute Exhaustive' : 'Queue Exhaustive'}
                confirmVariant="warning"
                isConfirmLoading={requestMutation.isPending}
                isOpen={confirmExhaustiveOpen}
                message="Exhaustive feature-pair discovery can evaluate a very large candidate space and should only be used when a deep scan still needs broader coverage."
                title="Queue Exhaustive Feature Pair Discovery"
                onCancel={() => {
                    setConfirmExhaustiveOpen(false);
                    setPendingForceRerun(false);
                }}
                onConfirm={() => requestMutation.mutate({ forceRerun: pendingForceRerun })}
            />
        </div>
    );
};

export default FeatureInteractionFindingsCard;
